import { AuthApiClient } from './AuthApiClient'
import { AuthTokenManager } from './AuthTokenManager'
import {
  AuthError,
  type AuthResponse,
  type AuthUser,
  type LoginRequest,
  type RefreshResult,
  type RegisterRequest,
  type UpdateProfileRequest,
  type VRoidAuthUrlResponse,
} from './types'
import { isDefinitiveAuthError } from './utils'

class AuthServiceClass {
  private tokenManager = AuthTokenManager
  private apiClient = AuthApiClient

  constructor() {
    // 相互参照の初期化
    this.apiClient.init(
      () => this.tokenManager.refreshAccessToken(),
      () => this.tokenManager.clearUser(),
    )
    this.tokenManager.init(this.apiClient)
  }

  // --- TokenManager 委譲 ---

  setUser(user: AuthUser): void {
    this.tokenManager.setUser(user)
  }

  clearUser(): void {
    this.tokenManager.clearUser()
  }

  getCachedUser(): AuthUser | null {
    return this.tokenManager.getCachedUser()
  }

  isAuthenticated(): boolean {
    return this.tokenManager.isAuthenticated()
  }

  refreshAccessToken(): Promise<RefreshResult> {
    return this.tokenManager.refreshAccessToken()
  }

  migrateSession(): Promise<boolean> {
    return this.tokenManager.migrateSession()
  }

  // --- ApiClient 委譲 ---

  request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.apiClient.request<T>(endpoint, options)
  }

  publicRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.apiClient.publicRequest<T>(endpoint, options)
  }

  // --- 認証エンドポイント ---

  async login(request: LoginRequest): Promise<AuthUser> {
    return this.apiClient.wrapAuthError(async () => {
      const response = await this.apiClient.request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(request),
      })
      this.tokenManager.setUser(response.user)
      return response.user
    }, 'ログインに失敗しました')
  }

  async register(request: RegisterRequest): Promise<AuthResponse> {
    return this.apiClient.wrapAuthError(async () => {
      const response = await this.apiClient.request<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(request),
      })
      if (response.user) {
        this.tokenManager.setUser(response.user)
      }
      return response
    }, 'ユーザー登録に失敗しました')
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>(`/auth/verify-email?token=${token}`, {
          method: 'GET',
        }),
      'メールアドレスの確認に失敗しました',
    )
  }

  async verifyNotificationEmail(token: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.publicRequest<{ message: string }>(
          `/auth/verify-notification-email?token=${token}`,
          { method: 'GET' },
        ),
      '通知メールアドレスの確認に失敗しました',
    )
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>('/auth/resend-verification', {
          method: 'POST',
          body: JSON.stringify({ email }),
        }),
      '確認メールの再送信に失敗しました',
    )
  }

  async logout(): Promise<void> {
    try {
      await this.apiClient.request('/auth/logout', {
        method: 'POST',
      })
    } catch {
      // エラーは無視してログアウト処理を続行
    } finally {
      this.tokenManager.clearUser()
    }
  }

  async getCurrentUser(): Promise<AuthUser> {
    try {
      const response = await this.apiClient.request<{ user: AuthUser }>('/auth/me', {
        method: 'GET',
      })
      this.tokenManager.setUser(response.user)
      return response.user
    } catch (error) {
      // 一過性の失敗（通信断・5xx・リフレッシュの一時エラー）だけキャッシュで継続する。
      // 確定的な失効でもキャッシュを返すと、ログイン表示のまま認証付き API だけが
      // 失敗し続ける状態に固まる（xrift-backend #601）。
      const cached = this.tokenManager.getCachedUser()
      if (cached && !isDefinitiveAuthError(error)) return cached

      this.tokenManager.clearUser()
      if (error instanceof AuthError) throw error
      throw new AuthError({ message: 'ユーザー情報の取得に失敗しました' })
    }
  }

  async updateProfile(request: UpdateProfileRequest): Promise<AuthUser> {
    return this.apiClient.wrapAuthError(async () => {
      const response = await this.apiClient.request<{ user: AuthUser }>('/auth/me', {
        method: 'PATCH',
        body: JSON.stringify(request),
      })
      this.tokenManager.setUser(response.user)
      return response.user
    }, 'プロフィールの更新に失敗しました')
  }

  async getVRoidAuthUrl(): Promise<VRoidAuthUrlResponse> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<VRoidAuthUrlResponse>('/auth/vroid/authorize', { method: 'GET' }),
      'VRoid認証URLの取得に失敗しました',
    )
  }

  async registerNotificationEmail(email: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>('/auth/me/notification-email', {
          method: 'POST',
          body: JSON.stringify({ email }),
        }),
      '通知用メールアドレスの登録に失敗しました',
    )
  }

  async resendNotificationEmailVerification(): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>('/auth/me/notification-email/resend', {
          method: 'POST',
        }),
      '確認メールの再送信に失敗しました',
    )
  }

  async removeNotificationEmail(): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>('/auth/me/notification-email', {
          method: 'DELETE',
        }),
      '通知用メールアドレスの削除に失敗しました',
    )
  }

  async registerEmail(email: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>('/auth/me/email', {
          method: 'POST',
          body: JSON.stringify({ email }),
        }),
      'メールアドレスの登録に失敗しました',
    )
  }

  async resendEmailVerification(): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.request<{ message: string }>('/auth/me/email/resend', { method: 'POST' }),
      '確認メールの再送信に失敗しました',
    )
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.publicRequest<{ message: string }>('/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        }),
      'パスワードリセットメールの送信に失敗しました',
    )
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.apiClient.wrapAuthError(
      () =>
        this.apiClient.publicRequest<{ message: string }>('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({ token, password }),
        }),
      'パスワードのリセットに失敗しました',
    )
  }

  async loginWithVRoidCallback(): Promise<AuthUser> {
    return this.apiClient.wrapAuthError(() => this.getCurrentUser(), 'VRoidログインに失敗しました')
  }
}

export const AuthService = new AuthServiceClass()
export { AuthError }
